import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type ThemeMode = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'edive-theme'

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches
}

function resolve(mode: ThemeMode): ResolvedTheme {
  return mode === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : mode
}

/** Scrive l'attributo che pilota sia i token CSS sia la variante Tailwind `dark:`. */
function apply(mode: ThemeMode): ResolvedTheme {
  const resolved = resolve(mode)
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', resolved)
  }
  return resolved
}

interface ThemeState {
  mode: ThemeMode
  resolved: ResolvedTheme
  setMode: (mode: ThemeMode) => void
  toggle: () => void
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'system',
      resolved: resolve('system'),
      setMode: (mode) => set({ mode, resolved: apply(mode) }),
      // Toggle esplicito: dal tema RISOLTO corrente passa all'opposto (forzato).
      toggle: () => {
        const next: ThemeMode = get().resolved === 'dark' ? 'light' : 'dark'
        set({ mode: next, resolved: apply(next) })
      },
    }),
    {
      name: STORAGE_KEY,
      partialize: (s) => ({ mode: s.mode }),
      onRehydrateStorage: () => (state) => {
        if (state) state.resolved = apply(state.mode)
      },
    },
  ),
)

/**
 * Da chiamare UNA VOLTA all'avvio, prima del render, per evitare il flash di
 * tema sbagliato (FOUC) e per reagire al cambio di tema di sistema quando
 * l'utente è su "system".
 */
export function initTheme(): void {
  let mode: ThemeMode = 'system'
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      const saved = parsed?.state?.mode
      if (saved === 'light' || saved === 'dark' || saved === 'system') mode = saved
    }
  } catch {
    /* localStorage non disponibile / JSON corrotto: resta "system" */
  }
  apply(mode)

  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      // Rileva il cambio di sistema solo se l'utente non ha forzato un tema.
      if (useThemeStore.getState().mode === 'system') useThemeStore.getState().setMode('system')
    })
  }
}

/** Hook ergonomico per i componenti (es. ThemeToggle). */
export function useTheme() {
  const mode = useThemeStore((s) => s.mode)
  const resolved = useThemeStore((s) => s.resolved)
  const setMode = useThemeStore((s) => s.setMode)
  const toggle = useThemeStore((s) => s.toggle)
  return { mode, resolved, setMode, toggle }
}
